import { buildApiUrl } from './api';

const parseJsonText = (text) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(text || 'Invalid server response');
  }
};

const isSameOriginRequest = (path) => {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URL(buildApiUrl(path), window.location.origin).origin === window.location.origin;
};

const submitViaIframeTransport = (path, payload, method = 'POST') =>
  new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Iframe submission is not available'));
      return;
    }

    const requestId = `lead-submit-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const iframeName = `${requestId}-frame`;
    const iframe = document.createElement('iframe');
    const form = method === 'POST' ? document.createElement('form') : null;
    const transportPayload = {
      ...payload,
      _transport: 'iframe',
      _requestId: requestId,
      _parentOrigin: window.location.origin,
    };
    let settled = false;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      iframe.remove();
      form?.remove();
    };

    const finish = (callback) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      cleanup();
      callback();
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const message = event.data;
      if (!message || message.__leadSubmit !== true || message.requestId !== requestId) {
        return;
      }

      const result = message.payload || {};
      finish(() => {
        if (result.status !== 'success') {
          reject(new Error(result.detail || result.message || 'Submission failed'));
          return;
        }

        resolve(result);
      });
    };

    const timeoutId = window.setTimeout(() => {
      finish(() => reject(new Error('Iframe submission timed out')));
    }, 30000);

    iframe.name = iframeName;
    iframe.style.display = 'none';
    window.addEventListener('message', handleMessage);
    document.body.appendChild(iframe);

    if (method === 'GET') {
      const url = new URL(buildApiUrl(path), window.location.origin);
      Object.entries(transportPayload).forEach(([key, value]) => {
        url.searchParams.set(key, value ?? '');
      });
      iframe.src = url.toString();
      return;
    }

    form.method = 'POST';
    form.action = buildApiUrl(path);
    form.target = iframeName;
    form.style.display = 'none';

    Object.entries(transportPayload).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value ?? '';
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  });

const submitViaXhr = (path, payload) =>
  new Promise((resolve, reject) => {
    if (typeof XMLHttpRequest === 'undefined') {
      reject(new Error('XMLHttpRequest is not available'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildApiUrl(path), true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.timeout = 15000;

    xhr.onload = () => {
      try {
        const result = parseJsonText(xhr.responseText || '');
        if (xhr.status < 200 || xhr.status >= 300 || result.status !== 'success') {
          reject(new Error(result.detail || result.message || 'Submission failed'));
          return;
        }
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    xhr.onerror = () => reject(new Error('XHR request failed'));
    xhr.ontimeout = () => reject(new Error('XHR request timed out'));
    xhr.send(JSON.stringify(payload));
  });

const submitViaHiddenForm = (path, payload) => submitViaIframeTransport(path, payload, 'POST');

const submitViaHiddenGet = (path, payload) => submitViaIframeTransport(path, payload, 'GET');

const submitViaFetch = async (path, payload) => {
  if (typeof fetch === 'undefined') {
    throw new Error('Fetch is not available');
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? window.setTimeout(() => controller.abort(), 10000)
    : null;

  try {
    const response = await fetch(buildApiUrl(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller?.signal,
    });

    const result = await response.json();
    if (!response.ok || result.status !== 'success') {
      throw new Error(result.detail || result.message || 'Submission failed');
    }

    return result;
  } finally {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  }
};

export const submitLead = async (path, payload) => {
  const canUseIframeFallback = typeof document !== 'undefined' && isSameOriginRequest(path);
  try {
    return await submitViaFetch(path, payload);
  } catch (fetchError) {
    const fetchMessage = String(fetchError?.message || '');
    const shouldTryFallback =
      typeof document !== 'undefined' &&
      (fetchError instanceof TypeError ||
        fetchError?.name === 'SyntaxError' ||
        fetchError?.name === 'AbortError' ||
        /fetch|network|load failed|timed out|abort/i.test(fetchMessage));

    if (!shouldTryFallback) {
      throw fetchError;
    }

    try {
      return await submitViaXhr(path, payload);
    } catch (xhrError) {
      const xhrMessage = String(xhrError?.message || '');
      const shouldTryHiddenForm =
        canUseIframeFallback &&
        /xhr|network|load failed|timed out|invalid/i.test(xhrMessage);

      if (!shouldTryHiddenForm) {
        throw xhrError;
      }

      return submitViaHiddenForm(path, payload);
    }
  }
};
